import React, { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext";
import { formatDateTime, getStatusLabel, getStatusTone } from "../lib/utils";

const initialForm = {
  trip_id: "",
  description: "",
  severity: "medium",
  status: "open",
};

export default function IncidentsPage() {
  const { user } = useAuth();
  const { incidents, trips, loadIncidents, loadTrips, createIncident, updateIncident } = useData();
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    loadIncidents();
    loadTrips();
  }, [loadIncidents, loadTrips]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.description) {
      toast.error("Describe the incident before submitting.");
      return;
    }
    const result = await createIncident({ ...form, trip_id: form.trip_id || null });
    if (result.success) {
      toast.success("Incident recorded.");
      setForm(initialForm);
      loadIncidents();
    } else {
      toast.error(result.error);
    }
  };

  const handleStatusChange = async (incident, nextStatus) => {
    const result = await updateIncident(incident.id, { status: nextStatus });
    if (result.success) {
      toast.success("Incident updated.");
      loadIncidents();
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[0.92fr_1.08fr] animate-fade-in">
      <form onSubmit={handleSubmit} className="guardian-card bg-white p-6">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-rose-50 p-3 text-rose-700"><AlertTriangle size={20} /></div>
          <div>
            <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Log an incident</h2>
            <p className="mt-1 text-sm text-[#64748B]">Capture safeguarding, operational, or route-related issues.</p>
          </div>
        </div>
        <div className="mt-6 grid gap-4">
          <div>
            <label className="text-sm font-medium text-[#0F172A]">Trip</label>
            <select value={form.trip_id} onChange={(event) => setForm((current) => ({ ...current, trip_id: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm">
              <option value="">General incident</option>
              {trips.map((trip) => (
                <option key={trip.id} value={trip.id}>{trip.child_name} • {trip.vehicle_registration || "Vehicle pending"}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-[#0F172A]">Severity</label>
            <select value={form.severity} onChange={(event) => setForm((current) => ({ ...current, severity: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-[#0F172A]">Description</label>
            <textarea value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} className="mt-2 min-h-[140px] w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
          </div>
        </div>
        <button type="submit" className="mt-6 rounded-xl bg-[#163D6B] px-4 py-3 text-sm font-semibold text-white">Submit incident</button>
      </form>

      <div className="guardian-card overflow-hidden bg-white">
        <div className="border-b border-[#E2E8F0] px-6 py-4">
          <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Incident register</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-[#64748B]">
              <tr>
                <th className="px-6 py-3 font-medium">Description</th>
                <th className="px-6 py-3 font-medium">Trip</th>
                <th className="px-6 py-3 font-medium">Severity</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Reported</th>
              </tr>
            </thead>
            <tbody>
              {incidents.length ? (
                incidents.map((incident) => (
                  <tr key={incident.id} className="border-t border-[#E2E8F0] align-top">
                    <td className="px-6 py-4 text-[#0F172A]">
                      <p className="font-medium">{incident.description}</p>
                      <p className="mt-1 text-xs text-[#64748B]">Reported by {incident.reported_by_name || "CareFleet user"}</p>
                    </td>
                    <td className="px-6 py-4 text-[#475569]">{incident.trip?.child_name || "General"}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${getStatusTone(incident.severity === "critical" ? "open" : incident.severity === "high" ? "investigating" : "pending")}`}>
                        {incident.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-[#475569]">
                      {user?.role === "admin" ? (
                        <select value={incident.status} onChange={(event) => handleStatusChange(incident, event.target.value)} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-xs">
                          <option value="open">Open</option>
                          <option value="investigating">Investigating</option>
                          <option value="resolved">Resolved</option>
                        </select>
                      ) : (
                        getStatusLabel(incident.status)
                      )}
                    </td>
                    <td className="px-6 py-4 text-[#475569]">{formatDateTime(incident.timestamp)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-[#64748B]">No incidents recorded.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
